import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Container,
  Paper,
  Divider
} from '@mui/material';
import { 
  PersonAdd as UserPlus, 
  Business as Building2, 
  Group as Users, 
  Security as Shield 
} from '@mui/icons-material';
import api from '../../utils/api';

const TeamMemberRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inviteData, setInviteData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing invitation token');
      setLoading(false);
      return;
    }

    fetchInviteDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [token]);

  const fetchInviteDetails = async () => {
    try {
      const response = await api.get(`/api/business/invite/${token}`);
      setInviteData(response.data);
    } catch (error) {
      setError(error.response?.data?.message || 'Invalid or expired invitation');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);

    try {
      const response = await api.post(`/api/business/invite/${token}/accept`, {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        password: formData.password
      });

      // Store the token and redirect to supplier dashboard
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userType', 'supplier');
      api.defaults.headers.common['x-auth-token'] = response.data.token;

      // Redirect to supplier dashboard
      navigate('/supplier/dashboard');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to accept invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleDescription = (role) => {
    switch (role) {
      case 'staff':
        return 'View your assigned bookings and schedule, access team communication tools, and update your availability.';
      case 'manager':
        return 'Manage bookings and team schedules, view business analytics, invite team members, and access all business features except settings.';
      case 'owner':
        return 'Full access to all business features, manage business settings and billing, invite and manage all team members, and complete business administration.';
      default:
        return 'Access to team features and collaboration tools.';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'staff':
        return <Users sx={{ fontSize: 20 }} />;
      case 'manager':
        return <Shield sx={{ fontSize: 20 }} />;
      case 'owner':
        return <Building2 sx={{ fontSize: 20 }} />;
      default:
        return <UserPlus sx={{ fontSize: 20 }} />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error && !inviteData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate('/')}
          fullWidth
        >
          Go to Homepage
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <UserPlus sx={{ fontSize: 48, color: '#6a1b9a', mb: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Join the Team
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete your registration to join {inviteData?.business?.name}
            </Typography>
          </Box>

          {inviteData && (
            <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getRoleIcon(inviteData.role)}
                <Typography variant="h6" sx={{ ml: 1 }}>
                  {inviteData.role.charAt(0).toUpperCase() + inviteData.role.slice(1)} Role
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {getRoleDescription(inviteData.role)}
              </Typography>
            </Paper>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                disabled={submitting}
              />
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                disabled={submitting}
              />
            </Box>

            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              disabled={submitting}
              sx={{ mb: 3 }}
              helperText="Password must be at least 6 characters long"
            />

            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              required
              disabled={submitting}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="large"
              disabled={submitting}
              sx={{ mb: 2 }}
            >
              {submitting ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Creating Account...
                </>
              ) : (
                'Join Team & Create Account'
              )}
            </Button>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary" textAlign="center">
            By joining, you agree to Idotoo's Terms of Service and Privacy Policy.
            You'll be able to access your team dashboard and start collaborating immediately.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default TeamMemberRegistration;